<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import { ref, computed, defineProps, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification";
import { useStore } from 'vuex'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCross from '../icon/IconCross.vue'
import IconChevronDown from '../icon/IconChevronDown.vue'
import MediaflowService from '@/services/MediaflowService';
import StreamSourceDropdown from "@/components/ui/StreamSourceDropdown.vue"
import FormSwitch from "@/components/Atoms/FormSwitch.vue"
import FormUpload from '../Atoms/FormUpload.vue';
import AlertModal from "@/components/modals/AlertModal.vue";
import Alert from '@/components/Atoms/Alert.vue';
import Sidebar from '../modals/Sidebar'

const props = defineProps({
  mediaflowId: {
    type: String,
    default: '',
    required: true
  },
  flowId: {
    type: String,
    default: '',
    required: true
  }
})

const store = useStore()
const { notify } = useNotification()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const sidebar = ref()
const error = ref(null)
const mediaTypeDropdown = ref(null)
const selectedMediaType = ref({
  label: 'Livestream',
  value: 'live'
})
const streamSourceDropdownVisible = ref(false)
const streamSourceDropdownRef = ref(null)

const liveRestartPlayback = ref(false)
const selectedStream = ref(null)

const loopVOD = ref(false)
const restartPlaybackVOD = ref(false)
const showEmptyOnEndVOD = ref(false)

const maxPosterUploadMB = ref(2)
const embedWatermarkTemp = ref(null)
const embedWatermarkTempImageId = ref(null)
const embedWatermarkTempUrl = ref(null)

const sourceUrl = ref(null)

const features = ref({
  embedWatermark: {
    error: false,
    enabled: false,
    value: null,
    valueType: 'string'
  }
})

const featureProcessing = ref({
  embedWatermark: false,
})

const mediaTypes = [
  { label: 'Livestream', value: 'live' },
  { label: 'Image', value: 'image' },
  { label: 'VOD', value: 'vod' },
  { label: 'URL', value: 'url' },
]
const media = ref({
  name: '',
  type: mediaTypes[0].value,
})

const createMediaValidation = computed(() => {
  if (media.value.name.length === 0) {
    return true
  }

  if (selectedMediaType.value.value === 'live' || selectedMediaType.value.value === 'vod') {
    return !selectedStream.value
  }

  if (selectedMediaType.value.value === 'image') {
    return !embedWatermarkTempImageId.value || !embedWatermarkTempUrl.value
  }

  if (selectedMediaType.value.value === 'url') {
    return !sourceUrl.value
  }

  return false
})

const emit = defineEmits(['media-added'])

const imageUrl = computed(() => {
  if (embedWatermarkTemp.value) {
    return embedWatermarkTemp.value
  }

  return null
})

const onEmbedPosterPreview = (file, name) => {
  const selector = '#embed-watermark-input';
  const imageInput = document.querySelector(selector);
  const imageFile = imageInput.files[0];

  if (!imageFile) return;

  if (imageFile.type.indexOf('image/') === -1) {
    showModal('embed-image-mime-type-add-media');
    imageInput.value = null;
    return;
  }

  let maxSize = 2
  maxPosterUploadMB.value = maxSize;

  maxSize *= 1024 * 1024;

  if (imageFile.size > maxSize) {
    showModal('embed-image-size-exceed-add-media');
    imageInput.value = null;
    return;
  }

  const validImageTypes = ['png', 'jpeg', 'gif', 'avif']
  const uploadedImageFileType = imageFile.type.replace('image/', '');
  if (
    validImageTypes &&
    validImageTypes.indexOf(uploadedImageFileType) === -1
  ) {
    showModal('embed-image-mime-type-add-media');
    imageInput.value = null;
    return;
  }

  imageReader(imageFile, base64 => {
    embedWatermarkTemp.value = base64;
  })
}

const saveEmbedPoster = async (type) => {
  const featureProp = 'embedWatermark'
  features.value[featureProp].error = false;
  const selector = '#embed-watermark-input'
  const imageInput = document.querySelector(selector);

  const imageFile = imageInput.files[0];
  if (!imageInput.value || !imageFile) {
    features.value[featureProp].error = 'please pick an image file';
    return;
  }

  featureProcessing.value[featureProp] = true;
  const fdata = new FormData();
  fdata.append('file', imageFile);
  const res = await MediaflowService.uploadFlowImage(
    props.mediaflowId,
    props.flowId,
    fdata
  );
  if (res.success) {
    embedWatermarkTempImageId.value = res.imageId
    embedWatermarkTempUrl.value = res.imageUrl

    notify({
      group: 'success',
      text: `image uploaded successfully.`
    });
    imageInput.value = ''
  } else {
    features.value[featureProp].error = 'Unable to upload the image. Please try again later.';
  }
  featureProcessing.value[featureProp] = false;
}

const cancelUpload = async (type) => {
  const selector = '#embed-watermark-input'
  const previewFlag = embedWatermarkTemp.value
  if (previewFlag) {
    embedWatermarkTemp.value = null;
    document.querySelector(selector).value = null;
  }
  removeImage(true);
}

const removeImage = async (toast = false) => {
  if (!embedWatermarkTempImageId.value) {
    return
  }

  try {
    await MediaflowService.deleteFlowImage(
      props.mediaflowId,
      props.flowId,
      embedWatermarkTempImageId.value
    )
    embedWatermarkTempImageId.value = null
    embedWatermarkTempUrl.value = null

    if (toast) {
      notify({ group: 'success', text: 'Image media removed' })
    }
    const settingsProp = 'embedWatermark'
    features.value[settingsProp].value = null;
  } catch (e) {
    console.log(e);
  }
}

function imageReader(file, cb) {
  if (!file) {
    return;
  }
  var reader = new FileReader();
  reader.onload = function () {
    if (cb) {
      cb(reader.result);
    }
  };
  reader.readAsDataURL(file);
}

const closeModal = () => {
  sidebar.value.closeSidebar()
}
const toggleSidebar = () => {
  sidebar.value.toggleSidebar()
}
const onReset = () => {
  media.value = { name: '', type: mediaTypes[0].value }
  error.value = null
  selectedStream.value = null
  embedWatermarkTemp.value = null

  embedWatermarkTempImageId.value = null
  embedWatermarkTempUrl.value = null

  sourceUrl.value = null

  selectedMediaType.value = mediaTypes[0]

  streamSourceDropdownRef.value.closeDropdown()
  streamSourceDropdownVisible.value = false

  removeImage(false)
}

const onMediaTypecSelect = (typ) => {
  selectedMediaType.value = typ
  selectedStream.value = null
  mediaTypeDropdown.value.hide(true);
}

const onSourceDropdownShow = () => {
  streamSourceDropdownVisible.value = true
}

const onSourceDropdownHide = () => {
  streamSourceDropdownVisible.value = false
}

const onSourceStreamSelected = async (stream) => {
  console.log('onSourceStreamSelected', stream)
  selectedStream.value = stream
}

const createMedia = async () => {
  try {
    const payload = {
      name: media.value.name,
      type: selectedMediaType.value.value,
    }

    if (payload.type === 'live') {
      payload.stream = selectedStream.value._id
      // payload.liveRestartPlayback = liveRestartPlayback.value
    }

    if (payload.type === 'vod') {
      payload.stream = selectedStream.value.file._id
      payload.key = selectedStream.value.folder.key
      payload.file = selectedStream.value.file.id
      payload.fileNmae = selectedStream.value.file.fileNmae
      payload.loop = loopVOD.value
      payload.restartPlayback = restartPlaybackVOD.value
      payload.showEmptyOnEnd = showEmptyOnEndVOD.value

      if (selectedStream.value?.file?.downloadUrl) {
        //live to vod
        payload.downloadUrl = selectedStream.value.file.downloadUrl
        payload.refId = selectedStream.value.file.refId
      }
    }

    if (payload.type === 'image') {
      payload.imageId = embedWatermarkTempImageId.value
      payload.imageUrl = embedWatermarkTempUrl.value
    }

    if (payload.type === 'url') {
      payload.sourceUrl = sourceUrl.value
    }

    console.log('createmedia', payload, props.mediaflowId, props.flowId)
    const newMedia = await MediaflowService.createFlowMedia(props.mediaflowId, props.flowId, payload)
    console.log('new media', newMedia)
    emit('media-added', newMedia)
    notify({ group: 'success', text: 'New Media Added' })
    closeModal()
  } catch (err) {
    error.value = err.message
  }
}

const onSidebarClosed = () => {
  onReset()
}

defineExpose({ toggleSidebar })
</script>

<template>
  <Sidebar ref="sidebar" :stage="1" @closed="onReset" width="md:w-[420px]" stageOneCustomClass="lg:h-[80vh]">
    <template v-slot:stage-one>
      <div class="px-4 h-full overflow-y-auto">
        <h3 class="text-lg font-medium mb-2 mt-3">Add Media</h3>
        <div v-if="error" class="text-red-600 text-sm mb-2">{{ error }}</div>

        <div class="mb-3">
          <label class="text-dark-9 text-tiny">Name</label>
          <FormInput v-model="media.name" placeholder="Media name" />
        </div>

        <div class="mb-3">
          <label class="text-dark-9 text-tiny">Type</label>
          <drop-down-menu ref="mediaTypeDropdown" type="flat" fullWidth>
            <template #toggle-button>
              {{ selectedMediaType.label }}
              <icon-base class="ml-auto">
                <icon-chevron-down />
              </icon-base>
            </template>

            <template #menu-items>
              <drop-down-menu-item v-for="option in mediaTypes" :key="option.value" @click="onMediaTypecSelect(option)">
                <span class="ml-2">{{ option.label }}</span>
              </drop-down-menu-item>
            </template>
          </drop-down-menu>
        </div>

        <div v-if="selectedMediaType.value === 'live'" class="mb-3">
          <label class="text-dark-9 text-tiny">Livestream</label>
          <stream-source-dropdown ref="streamSourceDropdownRef" @on-show="onSourceDropdownShow"
            @on-hide="onSourceDropdownHide" @on-source-selected="onSourceStreamSelected"
            :selectedStream="selectedStream" :dropRight="false" :onlyLiveSelector="true" :dropdownDisabled="false"
            source="multiview" :refLink="0" />
        </div>

        <div v-if="selectedMediaType.value === 'live'" class="px-3 mb-3">
          <!-- <FormSwitch size="md" label="Restart playback once scene becomes active" labelPosition="right" v-model="liveRestartPlayback" /> -->
        </div>

        <div v-if="selectedMediaType.value === 'vod'" class="px-3 mb-3">
          <label class="text-dark-9 text-tiny">Video from VODs</label>
          <stream-source-dropdown ref="streamSourceDropdownRef" @on-show="onSourceDropdownShow"
            @on-hide="onSourceDropdownHide" @on-source-selected="onSourceStreamSelected"
            :selectedStream="selectedStream" :dropRight="false" source="mediaflow" :showPlaylist="false"
            :hideLive="true" :refLink="0" />
        </div>

        <div v-if="selectedMediaType.value === 'vod'" class="px-3 mb-3">
          <FormSwitch size="md" label="Loop" labelPosition="right" v-model="loopVOD" />
        </div>

        <div v-if="selectedMediaType.value === 'vod'" class="px-3 mb-3">
          <FormSwitch size="md" label="Restart playback once scene becomes active" labelPosition="right"
            v-model="restartPlaybackVOD" />
        </div>

        <div v-if="selectedMediaType.value === 'vod'" class="px-3 mb-3">
          <FormSwitch size="md" label="Show nothing when playback ends" labelPosition="right"
            v-model="showEmptyOnEndVOD" />
        </div>

        <div v-if="selectedMediaType.value === 'image'" class="px-3 mb-3">
          <div class="basis-full md:basis-auto">
            <label class="text-dark-9 text-tiny">Upload Image</label>
          </div>
          <div class="flex justify-between items-center gap-x-2">
            <div class="flex">
              <img v-if="imageUrl" width="100px" :src="imageUrl" class="w-full size-full md:w-[100px]"  />
              <p v-else class="text-tiny text-surface-6">No file chosen</p>
            </div>

            <div class="flex">
              <FormUpload :class="{ hidden: embedWatermarkTemp }" name="watermark" id="embed-watermark-input"
                placeholder="No file chosen" type="secondary" :label="embedWatermarkTemp ? 'Change' : 'Select image'"
                :on-file-change="onEmbedPosterPreview" :accept-type="'image/*'" />
              <FormButton v-if="embedWatermarkTemp" :class="{ hidden: embedWatermarkTempImageId }"
                :disabled="featureProcessing.embedWatermark" @click="saveEmbedPoster('watermark')">
                {{ featureProcessing.embedWatermark ? 'Saving' : 'Save' }}
              </FormButton>
              <FormButton v-if="embedWatermarkTemp || features.embedWatermark.value" class="ml-2"
                :disabled="featureProcessing.embedWatermark" type="secondary" @click="cancelUpload('watermark')">
                <span v-if="embedWatermarkTemp">Cancel</span>
                <span v-else>Remove</span>
              </FormButton>
            </div>
          </div>
          <Alert v-show="features.embedWatermark.error" class="alert alert-danger">
            {{ features.embedWatermark.error }}
          </Alert>
        </div>

        <div v-if="selectedMediaType.value === 'url'" class="px-3 mb-3">
          <label class="text-dark-9 text-tiny">URL</label>
          <FormInput v-model="sourceUrl" placeholder="https://.." />
        </div>

        <div>
          <div class="text-center">
            <FormButton :disabled="createMediaValidation" @click="createMedia" type="main" size="md"
              class="w-full mt-4 justify-center">
              Create
            </FormButton>
            <FormButton type="secondary" size="md" class="w-full mb-3 mt-2 justify-center" @click="closeModal">
              Cancel
            </FormButton>
          </div>
        </div>
      </div>
    </template>
  </Sidebar>

  <alert-modal :message="'Uploaded image too large. Image must not exceed ' + maxPosterUploadMB + 'MB'"
    modal-id="embed-image-size-exceed-add-media" />
  <alert-modal modal-id="embed-image-mime-type-add-media" message="Only PNG/JPG/GIF/AVIF image files are allowed" />
</template>